import { extend } from 'flarum/common/extend';
import Model from 'flarum/common/Model';
import app from 'flarum/forum/app';
import IndexPage from 'flarum/forum/components/IndexPage';
import DiscussionPage from 'flarum/forum/components/DiscussionPage';
import DiscussionHero from 'flarum/forum/components/DiscussionHero';
import PostStream from 'flarum/forum/components/PostStream';
import SettingsPage from 'flarum/forum/components/SettingsPage';
import PostControls from 'flarum/forum/utils/PostControls';
import Discussion from 'flarum/common/models/Discussion';
import DiscussionListItem from 'flarum/forum/components/DiscussionListItem';
import TaroCase from './models/TaroCase';
import CaseAiReport from './models/CaseAiReport';
import CaseInterpretation from './models/CaseInterpretation';
import CaseMentorReview from './models/CaseMentorReview';
import CaseFeedback from './models/CaseFeedback';
import CaseFollowUp from './models/CaseFollowUp';
import CaseComposer from './components/CaseComposer';
import CaseAgentComposer from './components/CaseAgentComposer';
import MyCasesPage from './components/MyCasesPage';
import DiscussionComposer from 'flarum/forum/components/DiscussionComposer';
import PublishCasePage from './components/PublishCasePage';
import TaroCaseModules from './components/TaroCaseModules';
import DefaultSettingsModal from './components/DefaultSettingsModal';
import CaseAiReportReadyNotification from './components/CaseAiReportReadyNotification';
import CaseMentorReviewRequestedNotification from './components/CaseMentorReviewRequestedNotification';
import CaseFollowUpAnsweredNotification from './components/CaseFollowUpAnsweredNotification';

// Register the `taroCase` has-one relation on Discussion so that
// `discussion.taroCase()` is callable from the frontend. PHP `Extend\Model`
// does NOT auto-sync to the frontend, so we must add the relation here.
Object.assign(Discussion.prototype, {
  taroCase: Model.hasOne('taroCase'),
  taroCaseAiReady: Model.attribute('taroCaseAiReady'),
  taroCaseVisibility: Model.attribute('taroCaseVisibility'),
  taroCaseId: Model.attribute('taroCaseId'),
});

app.initializers.add('taro-case', () => {
  // 个人中心「我的案例」独立页面（/u/:username/cases，与 user.taroCredits 风格一致）
  app.routes['user.taroCases'] = {
    path: '/u/:username/cases',
    component: MyCasesPage,
  };
  // 兼容别名：/my-cases（后端 route 已注册，直链/刷新可达）
  app.routes['taroMyCases'] = {
    path: '/my-cases',
    component: MyCasesPage,
  };
  // 案例发布页面（左栏按钮跳转中间内容区）
  app.routes['taroPublishCase'] = {
    path: '/publish-case',
    component: PublishCasePage,
  };

  // 注册自定义通知类型的前端渲染组件
  app.notificationComponents['caseAiReportReady'] = CaseAiReportReadyNotification;
  app.notificationComponents['caseMentorReviewRequested'] = CaseMentorReviewRequestedNotification;
  app.notificationComponents['caseFollowUpAnswered'] = CaseFollowUpAnsweredNotification;

  // CRITICAL: model registry keys MUST match JSON:API `type` returned by the
  // API, which is kebab-case plural (see how Flarum core registers
  // 'discussions', 'posts', etc. in common/Application.tsx). Registering under
  // the wrong key means Store.pushObject silently returns null and your
  // model data is dropped — which causes `taroCase.feedbacks()` to return
  // an array of undefined, etc.
  app.store.models['taro-cases'] = TaroCase;
  app.store.models['taro-case-ai-reports'] = CaseAiReport;
  app.store.models['taro-case-interpretations'] = CaseInterpretation;
  app.store.models['taro-case-mentor-reviews'] = CaseMentorReview;
  app.store.models['taro-case-feedbacks'] = CaseFeedback;
  app.store.models['taro-case-follow-ups'] = CaseFollowUp;

  // 案例发布权限检查：仅大导师/辅导员/成员组（id=10）可发布案例
  // 受限用户（id=3）默认无此权限
  function canPublishTaroCase() {
    const user = app.session.user;
    if (!user) return false;
    if (user.isAdmin()) return true;
    if (user.attribute('taroIsModerator')) return true;
    // 成员组 id=10
    const groups = user.groups();
    if (groups) {
      for (const g of groups) {
        if (g.id && g.id() === 10) return true;
      }
    }
    return false;
  }

  // 替换 Flarum 核心"发布主题"为"发布案例" + 加"发起工单"按钮
  extend(IndexPage.prototype, 'sidebarItems', function (items) {
    if (!app.session.user) return;

    // 1. "发布主题"->"发布案例"（CaseComposer）；无权限则隐藏
    if (items.has('newDiscussion')) {
      if (canPublishTaroCase()) {
        items.replace('newDiscussion', (
          <button
            className="Button Button--primary IndexPage-newDiscussion"
            onclick={() => {
              app.composer.load(CaseComposer);
              app.composer.show();
            }}
          >
            <i className="fas fa-edit" /> 发布案例
          </button>
        ));
      } else {
        items.remove('newDiscussion');
      }
    }

    // 2. "发起工单"（限定 tag=ticket 的普通主题）
    items.add(
      'newTicket',
      (
        <button
          className="Button"
          style="width:100%;margin-bottom:8px;"
          onclick={() => {
            const ticketTag = app.store.all('tags').find((t) => t.slug() === 'ticket');
            app.composer.show(DiscussionComposer, {
              tags: ticketTag ? [ticketTag] : [],
            });
          }}
        >
          <i className="fas fa-ticket-alt" /> 发起工单
        </button>
      ),
      -20
    );
  });

  // Ensure the discussion fetch includes posts + posts.user so the poststream
  // always renders, even when SSR returns a 404 page (restricted forums) and
  // the fallback `app.store.find` is taken with our include params.
  extend(DiscussionPage.prototype, 'requestParams', function (params) {
    const wanted = [
      'posts',
      'posts.user',
      'tags',
      'tags.parent',
      'tags.state',
      'taroCase',
      'taroCase.aiReport',
      'taroCase.interpretation',
      'taroCase.mentorReview',
      'taroCase.feedbacks',
      'taroCase.followUps',
      'taroCase.followUps.user',
      'taroCase.followUps.answeredBy',
      'taroCase.purchases',
    ];
    const existing = typeof params.include === 'string' ? params.include : '';
    const set = new Set(existing ? existing.split(',') : []);
    wanted.forEach((rel) => set.add(rel));
    params.include = Array.from(set).join(',');
  });

  // Inject the taro-case 3-section accordion AFTER the first post using
  // PostStream's official extension point. This guarantees the modules sit
  // between the main post (#1) and the first reply (#2), regardless of
  // Flarum's mainContent / pageContent layout.
  extend(PostStream.prototype, 'afterFirstPostItems', function (items) {
    if (!this.discussion) return;
    const taroCase = this.discussion.taroCase();
    if (!taroCase) return;

    items.add(
      'taro-case-modules',
      <TaroCaseModules case={taroCase} />,
      10
    );
  });

  // 在 SettingsPage 添加"默认便捷"Tab
  extend(SettingsPage.prototype, 'settingsItems', function (items) {
    if (!app.session.user) return;
    items.add(
      'taroDefaultSettings',
      <button
        className="Button"
        onclick={() => app.modal.show(DefaultSettingsModal)}
      >
        <i className="fas fa-sliders-h"></i> 塔罗牌·默认便捷设置
      </button>,
      80
    );
  });

  // 在 DiscussionListItem（列表项）添加 AI 解读标记
  if (DiscussionListItem && DiscussionListItem.prototype) {
    extend(DiscussionListItem.prototype, 'infoItems', function (items) {
      const discussion = this.attrs && this.attrs.discussion;
      if (!discussion) return;
      const taroCaseId = discussion.attribute && discussion.attribute('taroCaseId');
      if (!taroCaseId) return;

      const aiReady = discussion.attribute('taroCaseAiReady');
      const visibility = discussion.attribute('taroCaseVisibility');

      if (aiReady) {
        items.add(
          'taroCaseAiReady',
          <span className="TaroCaseBadge TaroCaseBadge--ai-ready" title="AI 解读已生成">
            <i className="fas fa-robot"></i> AI 已解读
          </span>,
          50
        );
      } else {
        items.add(
          'taroCaseAiPending',
          <span className="TaroCaseBadge TaroCaseBadge--ai-pending" title="AI 解读待生成">
            <i className="fas fa-robot"></i> AI 待解读
          </span>,
          50
        );
      }

      if (visibility === 'private') {
        items.add(
          'taroCasePrivate',
          <span className="TaroCaseBadge TaroCaseBadge--private" title="仅案主、导师、辅导员可见">
            <i className="fas fa-lock"></i> 私密
          </span>,
          60
        );
      }
    });
  }

  // 在 DiscussionHero（帖子标题栏）移除了"编辑案例"和"公开/私密"按钮
  // 这些功能已移至萨拉解读模块内部工具栏

  // 拦截首贴"编辑"按钮：案例帖首贴编辑必须打开 CaseComposer 表单，
  // 避免用户用普通编辑器改动案例正文，导致 case_data 与帖子内容不同步。
  // 使用 Flarum 标准 extend 模式，避免 monkey-patching PostControls.editAction。
  extend(PostControls, 'editAction', function (original) {
    const post = this;
    const discussion = post.discussion && post.discussion();
    if (post.number && post.number() === 1 && discussion) {
      const taroCase = discussion.taroCase && discussion.taroCase();
      if (taroCase) {
        app.composer.load(CaseComposer, { case: taroCase });
        app.composer.show();
        return Promise.resolve();
      }
    }
    return original.call(this);
  });

  // ============================================================
  // Agent 停靠面板标签注册
  // taro-ai 的 AgentDockPanel 提供"萨拉"标签，此处注册：
  //   🔮 发案例（CaseAgentComposer：识图/文字/手动 → 确认 → 发布 → 自动解读跟帖）
  // 注：案例探讨（CaseChatPanel）已下线——被划词提问功能平替；
  //     组件与后端 conversation API 保留但不再注册入口。
  // ============================================================
  if (!app.taroAgentTabs) {
    app.taroAgentTabs = {
      _tabs: [],
      register(tab) {
        if (tab && tab.id && tab.component) {
          if (!this._tabs.find((t) => t.id === tab.id)) this._tabs.push(tab);
        }
      },
    };
  }

  // 任务 J：移除 case-composer 独立 tab（与萨拉 tab 重复）。发布案例统一走左栏按钮。
});
